/* eslint camelcase: ["error", {"allow": ["shortcut_id", "opened_by_creator"]}] -- Analytics schema fields. */
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

import type {SavedViewOpenSource} from '../saved-view-open-source'
import type {SavedViewId} from '../types'

const ANALYTICS_TARGET = 'REPOSITORY_SAVED_VIEW'

export const SAVED_VIEW_EVENTS = {
  indexVisited: 'repository_saved_view.index',
  newVisited: 'repository_saved_view.new',
  viewOpened: 'repository_saved_view.open',
  editOpened: 'repository_saved_view.edit',
  viewUpdated: 'repository_saved_view.update',
  viewPinned: 'repository_saved_view.pin',
  viewUnpinned: 'repository_saved_view.unpin',
} as const

export const SAVED_VIEW_UPDATE_SOURCES = {
  edit: 'edit',
  query: 'query',
} as const

export type SavedViewUpdateSource = (typeof SAVED_VIEW_UPDATE_SOURCES)[keyof typeof SAVED_VIEW_UPDATE_SOURCES]

export const SAVED_VIEW_PIN_SOURCES = {
  indexRowMenu: 'index_row_menu',
  individualViewActionsMenu: 'individual_view_actions_menu',
} as const

export type SavedViewPinSource = (typeof SAVED_VIEW_PIN_SOURCES)[keyof typeof SAVED_VIEW_PIN_SOURCES]

export function useSavedViewAnalytics() {
  const {sendAnalyticsEvent} = useAnalytics()

  const trackIndexVisited = useCallback(() => {
    sendAnalyticsEvent(SAVED_VIEW_EVENTS.indexVisited, ANALYTICS_TARGET)
  }, [sendAnalyticsEvent])

  const trackNewVisited = useCallback(() => {
    sendAnalyticsEvent(SAVED_VIEW_EVENTS.newVisited, ANALYTICS_TARGET)
  }, [sendAnalyticsEvent])

  const trackViewOpened = useCallback(
    (shortcutId: SavedViewId, source: SavedViewOpenSource, openedByCreator: boolean) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewOpened, ANALYTICS_TARGET, {
        shortcut_id: shortcutId,
        source,
        opened_by_creator: openedByCreator,
      })
    },
    [sendAnalyticsEvent],
  )

  const trackEditOpened = useCallback(
    (shortcutId: SavedViewId) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.editOpened, ANALYTICS_TARGET, {shortcut_id: shortcutId})
    },
    [sendAnalyticsEvent],
  )

  const trackViewUpdated = useCallback(
    (shortcutId: SavedViewId, source: SavedViewUpdateSource) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewUpdated, ANALYTICS_TARGET, {shortcut_id: shortcutId, source})
    },
    [sendAnalyticsEvent],
  )

  const trackViewPinned = useCallback(
    (shortcutId: SavedViewId, source: SavedViewPinSource) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewPinned, ANALYTICS_TARGET, {shortcut_id: shortcutId, source})
    },
    [sendAnalyticsEvent],
  )

  const trackViewUnpinned = useCallback(
    (shortcutId: SavedViewId, source: SavedViewPinSource) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewUnpinned, ANALYTICS_TARGET, {shortcut_id: shortcutId, source})
    },
    [sendAnalyticsEvent],
  )

  return {
    trackIndexVisited,
    trackNewVisited,
    trackViewOpened,
    trackEditOpened,
    trackViewUpdated,
    trackViewPinned,
    trackViewUnpinned,
  }
}
